import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";

const CurrencyContext = createContext(null);
export const useCurrency = () => useContext(CurrencyContext);

// Selected app language -> representative country (drives the displayed currency)
const LANG_COUNTRY = {
  fr: "FR", en: "US", es: "ES", es_mx: "MX", de: "DE", it: "IT",
  pl: "PL", fil: "PH", ru: "RU", el: "GR", hi: "IN", ja: "JP", zh: "CN",
};

const guessCountry = () => {
  try {
    const loc = (navigator.languages && navigator.languages[0]) || navigator.language || "";
    const parts = loc.split("-");
    if (parts.length > 1) return parts[1].toUpperCase();
  } catch (e) {}
  return null;
};

export const CurrencyProvider = ({ children }) => {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const [fx, setFx] = useState({ currency: "EUR", rate: 1, decimals: 2, symbol: "€", ready: false });

  const load = useCallback(async () => {
    try {
      const langKey = (i18n.language || "fr").replace("-", "_").toLowerCase();
      // Currency follows the chosen language first, then falls back to profile/browser country
      const country = LANG_COUNTRY[langKey] || LANG_COUNTRY[langKey.split("_")[0]] || (user && user.country) || guessCountry();
      const params = {};
      if (country) params.country = country;
      const r = await api.get("/fx/rate", { params });
      setFx({ ...r.data, ready: true });
    } catch (e) {
      setFx({ currency: "EUR", rate: 1, decimals: 2, symbol: "€", ready: true });
    }
  }, [user, i18n.language]);

  useEffect(() => { load(); }, [load]);

  const format = useCallback((eurCents) => {
    const { currency, rate, decimals, symbol } = fx;
    if (!currency || currency === "EUR") {
      const v = (eurCents || 0) / 100;
      return `${Number.isInteger(v) ? v : v.toFixed(2)}€`;
    }
    const converted = ((eurCents || 0) / 100) * (rate || 1);
    const amount = converted.toFixed(decimals || 0);
    const sym = symbol || currency;
    return `${amount} ${sym}`;
  }, [fx]);

  return (
    <CurrencyContext.Provider value={{ ...fx, format }}>
      {children}
    </CurrencyContext.Provider>
  );
};
